/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { UpdateActions } from '../../../../../actions/universalTrackerValue';
import { UniversalTrackerValuePlain } from '../../../../../types/universalTrackerValue';
import { SubmitVerifiedButtonState, SubmitDisabledReason, QuestionButtonTooltip, getSubmitDisabledReasonTooltip } from '../../constants';
import { isUtrvCompletedOpen } from '@utils/universalTrackerValue';

export const canSubmitAndVerifyAction = ({ status }: Pick<UniversalTrackerValuePlain, 'status'>, action: number) => {
  const { UPDATED, REJECTED, VERIFIED } = UpdateActions.ACTION.UTR;
  switch (action) {
    case SubmitVerifiedButtonState.Show:
      return [REJECTED, UPDATED, VERIFIED].includes(status);
    case SubmitVerifiedButtonState.Enable:
      return [REJECTED, VERIFIED].includes(status);
    default:
      return false;
  }
}

interface GetSubmitButtonTooltipParams {
  utrv: Pick<UniversalTrackerValuePlain, 'status' | 'assuranceStatus'>;
  isDisabled: boolean;
  isContributorAndVerifier: boolean;
  submitDisabledReasons: SubmitDisabledReason[];
}

export const getSubmitButtonTooltip = ({
  utrv: { status, assuranceStatus },
  isDisabled,
  isContributorAndVerifier,
  submitDisabledReasons,
}: GetSubmitButtonTooltipParams) => {
  const { CREATED, UPDATED, REJECTED, VERIFIED } = UpdateActions.ACTION.UTR;
  if (!isDisabled) {
    return isContributorAndVerifier ? QuestionButtonTooltip.SubmitWithoutVerification : '';
  }
  const reasonTooltip = getSubmitDisabledReasonTooltip(submitDisabledReasons);
  if (reasonTooltip) {
    return reasonTooltip;
  }
  if (isUtrvCompletedOpen({ assuranceStatus }) && isContributorAndVerifier) {
    return QuestionButtonTooltip.NoteRequiredOnRestatement;
  }
  switch (status) {
    case UPDATED:
      return isContributorAndVerifier ? QuestionButtonTooltip.UpdateToSubmit : QuestionButtonTooltip.NothingToSubmit;
    case VERIFIED:
      return isContributorAndVerifier ? QuestionButtonTooltip.NothingToSubmit : QuestionButtonTooltip.UnableToSubmit;
    case CREATED:
    case REJECTED:
    default:
      return QuestionButtonTooltip.NothingToSubmit;
  }
};

interface GetVerifyButtonTooltipParams {
  isDisabled: boolean;
  submitDisabledReasons: SubmitDisabledReason[];
}

export const getVerifyButtonTooltip = ({ isDisabled, submitDisabledReasons }: GetVerifyButtonTooltipParams) => {
  if (!isDisabled) {
    return '';
  }
  const reasonTooltip = getSubmitDisabledReasonTooltip(submitDisabledReasons);
  if (reasonTooltip) {
    return reasonTooltip;
  }
  return QuestionButtonTooltip.NothingToVerify;
}

export const getRejectButtonTooltip = (isDisabled: boolean) => {
  return isDisabled ? QuestionButtonTooltip.NoteRequiredOnRestatement : '';
};

interface GetSubmitAndVerifyButtonTooltipParams {
  utrv: Pick<UniversalTrackerValuePlain, 'verificationRequired' | 'assuranceStatus'>;
  isDisabled: boolean;
  submitDisabledReasons: SubmitDisabledReason[];
}

export const getSubmitAndVerifyButtonTooltip = ({
  utrv: { verificationRequired, assuranceStatus },
  isDisabled,
  submitDisabledReasons,
}: GetSubmitAndVerifyButtonTooltipParams) => {
  if (!isDisabled) {
    return verificationRequired ? QuestionButtonTooltip.SubmitAsVerification : '';
  }
  const reasonTooltip = getSubmitDisabledReasonTooltip(submitDisabledReasons);
  if (reasonTooltip) {
    return reasonTooltip;
  }
  if (isUtrvCompletedOpen({ assuranceStatus })) {
    return QuestionButtonTooltip.NoteRequiredOnRestatement;
  }
  if (verificationRequired) {
    return QuestionButtonTooltip.NothingToSubmitOrVerify;
  }
  return QuestionButtonTooltip.NothingToSubmit;
};
